import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import GrapeChipsField from "../components/wine/GrapeChipsField";
import PostPhoto from "../components/post/PostPhoto";
import useDocumentTitle from "../hooks/useDocumentTitle";
import {
  getUserWine,
  listTastings,
  listWineCountries,
  updateUserWine,
} from "../utils/apiClient";

const COLOR_LABELS = {
  red: "Rød",
  white: "Hvid",
  rose: "Rosé",
  orange: "Orange",
  other: "Andet",
};

const STYLE_LABELS = {
  still: "Stille",
  sparkling: "Mousserende",
  fortified: "Stærkvin",
  dessert: "Dessert",
  other: "Andet",
};

const CURRENT_YEAR = new Date().getFullYear();
const VINTAGE_YEARS = Array.from({ length: CURRENT_YEAR - 1900 + 1 }, (_, i) => CURRENT_YEAR - i);

function emptyWineForm() {
  return {
    name: "",
    producer_name: "",
    color: "red",
    style: "still",
    vintage_year: "",
    is_non_vintage: true,
    country_iso: "",
    region_name: "",
    grapes: [],
    description: "",
  };
}

function formFromItem(item) {
  const v = item?.vintage || {};
  return {
    name: v.wine_name || "",
    producer_name: v.producer_name || "",
    color: v.color_name || "red",
    style: v.style_name || "still",
    vintage_year: v.is_non_vintage ? "" : v.vintage_year || "",
    is_non_vintage: Boolean(v.is_non_vintage) || !v.vintage_year,
    country_iso: v.country_iso || "",
    region_name: v.geography_name || "",
    grapes: Array.isArray(v.grapes) ? [...v.grapes] : [],
    description: v.description || "",
  };
}

function InfoRow({ label, children }) {
  return (
    <Box
      component="tr"
      sx={{
        "&:nth-of-type(odd)": { bgcolor: "action.hover" },
      }}
    >
      <Box
        component="th"
        scope="row"
        sx={{
          textAlign: "left",
          fontWeight: 600,
          fontSize: "0.875rem",
          px: 1.5,
          py: 1.1,
          width: "32%",
          color: "text.secondary",
          verticalAlign: "top",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {label}
      </Box>
      <Box
        component="td"
        sx={{
          px: 1.5,
          py: 1.1,
          fontSize: "0.875rem",
          borderBottom: "1px solid",
          borderColor: "divider",
          verticalAlign: "top",
        }}
      >
        {children || "—"}
      </Box>
    </Box>
  );
}

function WineManagerDetail() {
  const { userWineUuid } = useParams();

  const [item, setItem] = useState(null);
  const [wine, setWine] = useState(emptyWineForm);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [isWantlist, setIsWantlist] = useState(false);

  const [countries, setCountries] = useState([]);
  const [tastings, setTastings] = useState([]);

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCollection, setSavingCollection] = useState(false);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const title = item?.vintage
    ? [item.vintage.producer_name, item.vintage.wine_name].filter(Boolean).join(" · ")
    : "Vin";
  useDocumentTitle(title);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getUserWine(userWineUuid);
      setItem(data);
      setWine(formFromItem(data));
      setQuantity(data.quantity ?? 0);
      setNotes(data.notes || "");
      setIsWantlist(Boolean(data.is_wantlist));

      const vintageUuid = data.wine_vintage_uuid;
      if (vintageUuid) {
        try {
          const tastingData = await listTastings({ wine_vintage_uuid: vintageUuid });
          setTastings(Array.isArray(tastingData) ? tastingData : []);
        } catch {
          setTastings([]);
        }
      } else {
        setTastings([]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke hente vin");
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [userWineUuid]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await listWineCountries();
        if (cancelled) return;
        setCountries(Array.isArray(c) ? c : []);
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const vintageSelectValue = wine.is_non_vintage || !wine.vintage_year ? "nv" : String(wine.vintage_year);

  const setVintageFromSelect = (value) => {
    if (value === "nv") {
      setWine((m) => ({ ...m, is_non_vintage: true, vintage_year: "" }));
    } else {
      setWine((m) => ({ ...m, is_non_vintage: false, vintage_year: Number(value) }));
    }
  };

  const canSaveWine = useMemo(
    () => Boolean(wine.name.trim() && wine.producer_name.trim()),
    [wine.name, wine.producer_name]
  );

  const countryName = useMemo(() => {
    if (!wine.country_iso) return item?.vintage?.country_name || "";
    return countries.find((c) => c.iso_code === wine.country_iso)?.name || wine.country_iso;
  }, [wine.country_iso, countries, item]);

  const startEdit = () => {
    setWine(formFromItem(item));
    setEditing(true);
    setSavedMsg("");
    setError("");
  };

  const cancelEdit = () => {
    setWine(formFromItem(item));
    setEditing(false);
    setError("");
  };

  const handleSaveWine = async (event) => {
    event.preventDefault();
    if (!canSaveWine || saving) return;
    setSaving(true);
    setError("");
    setSavedMsg("");
    try {
      const updated = await updateUserWine(userWineUuid, {
        wine: {
          name: wine.name.trim(),
          producer_name: wine.producer_name.trim(),
          color: wine.color,
          style: wine.style,
          vintage_year: wine.is_non_vintage ? null : Number(wine.vintage_year) || null,
          is_non_vintage: Boolean(wine.is_non_vintage),
          country_iso: wine.country_iso || null,
          region_name: wine.region_name.trim() || null,
          grapes: wine.grapes,
          description: wine.description.trim() || null,
        },
      });
      setItem(updated);
      setWine(formFromItem(updated));
      setEditing(false);
      setSavedMsg("Vin gemt");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke gemme vin");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCollection = async (event) => {
    event.preventDefault();
    if (savingCollection) return;
    setSavingCollection(true);
    setError("");
    setSavedMsg("");
    try {
      const updated = await updateUserWine(userWineUuid, {
        quantity: Number(quantity) || 0,
        notes: notes.trim() || null,
        is_wantlist: isWantlist,
      });
      setItem(updated);
      setQuantity(updated.quantity ?? 0);
      setNotes(updated.notes || "");
      setIsWantlist(Boolean(updated.is_wantlist));
      setSavedMsg("Samling gemt");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke gemme");
    } finally {
      setSavingCollection(false);
    }
  };

  const changeQuantity = async (raw) => {
    const next = Math.max(0, Number(raw) || 0);
    setQuantity(next);
    try {
      const updated = await updateUserWine(userWineUuid, {
        quantity: next,
      });
      setItem(updated);
      setQuantity(updated.quantity ?? next);
      setIsWantlist(Boolean(updated.is_wantlist));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke opdatere antal");
      setQuantity(item?.quantity ?? 0);
    }
  };

  const toggleWantlist = async () => {
    setError("");
    setSavedMsg("");
    try {
      const updated = await updateUserWine(userWineUuid, {
        is_wantlist: !isWantlist,
      });
      setItem(updated);
      setIsWantlist(Boolean(updated.is_wantlist));
      setSavedMsg(
        updated.is_wantlist ? "Tilføjet til ønskeliste" : "Fjernet fra ønskeliste"
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke opdatere ønskeliste");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 960 }}>
        <Button component={RouterLink} to="/tools/wine-manager" variant="text" sx={{ px: 0, mb: 1 }}>
          Tilbage
        </Button>
        <Typography color="error">{error || "Vin ikke fundet"}</Typography>
      </Box>
    );
  }

  const v = item.vintage || {};
  const yearLabel = v.is_non_vintage ? "NV" : v.vintage_year || "—";
  const tastingHref = `/wine-tasting/new?blind=0&user_wine_uuid=${encodeURIComponent(
    item.user_wine_uuid
  )}`;

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, pb: 10, maxWidth: 960 }}>
      <Button
        component={RouterLink}
        to="/tools/wine-manager"
        variant="text"
        sx={{ px: 0, mb: 1 }}
      >
        Tilbage
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            {v.wine_name || "Vin"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {[v.producer_name, yearLabel].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        {!editing ? (
          <Button variant="outlined" onClick={startEdit} sx={{ alignSelf: { sm: "center" } }}>
            Rediger
          </Button>
        ) : null}
      </Stack>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}
      {savedMsg ? (
        <Typography color="success.main" sx={{ mb: 2 }}>
          {savedMsg}
        </Typography>
      ) : null}

      {editing ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Rediger vin
          </Typography>
          {v.grapeminds_id ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              GrapeMinds id {v.grapeminds_id}
            </Typography>
          ) : null}
          <Box component="form" onSubmit={handleSaveWine}>
            <Stack spacing={1.5}>
              <TextField
                required
                size="small"
                label="Vin"
                value={wine.name}
                onChange={(e) => setWine((m) => ({ ...m, name: e.target.value }))}
              />
              <TextField
                required
                size="small"
                label="Producent"
                value={wine.producer_name}
                onChange={(e) => setWine((m) => ({ ...m, producer_name: e.target.value }))}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  select
                  size="small"
                  label="Farve"
                  value={wine.color}
                  onChange={(e) => setWine((m) => ({ ...m, color: e.target.value }))}
                  fullWidth
                >
                  <MenuItem value="red">Rød</MenuItem>
                  <MenuItem value="white">Hvid</MenuItem>
                  <MenuItem value="rose">Rosé</MenuItem>
                  <MenuItem value="orange">Orange</MenuItem>
                  <MenuItem value="other">Andet</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Stil"
                  value={wine.style}
                  onChange={(e) => setWine((m) => ({ ...m, style: e.target.value }))}
                  fullWidth
                >
                  <MenuItem value="still">Stille</MenuItem>
                  <MenuItem value="sparkling">Mousserende</MenuItem>
                  <MenuItem value="fortified">Stærkvin</MenuItem>
                  <MenuItem value="dessert">Dessert</MenuItem>
                  <MenuItem value="other">Andet</MenuItem>
                </TextField>
              </Stack>
              <TextField
                select
                size="small"
                label="Årgang"
                value={vintageSelectValue}
                onChange={(e) => setVintageFromSelect(e.target.value)}
                fullWidth
              >
                <MenuItem value="nv">Non-vintage</MenuItem>
                {VINTAGE_YEARS.map((year) => (
                  <MenuItem key={year} value={String(year)}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Land"
                value={wine.country_iso || ""}
                onChange={(e) => setWine((m) => ({ ...m, country_iso: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">
                  <em>Vælg land</em>
                </MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.iso_code} value={c.iso_code}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Region"
                value={wine.region_name}
                onChange={(e) => setWine((m) => ({ ...m, region_name: e.target.value }))}
              />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                  Druer
                </Typography>
                <GrapeChipsField
                  value={wine.grapes}
                  onChange={(grapes) => setWine((m) => ({ ...m, grapes }))}
                />
              </Box>
              <TextField
                size="small"
                label="Beskrivelse"
                value={wine.description}
                onChange={(e) => setWine((m) => ({ ...m, description: e.target.value }))}
                multiline
                minRows={2}
              />
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" disabled={!canSaveWine || saving}>
                  {saving ? <CircularProgress size={18} /> : "Gem vin"}
                </Button>
                <Button type="button" onClick={cancelEdit} disabled={saving}>
                  Annuller
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      ) : (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="stretch"
          sx={{ mb: 3 }}
        >
          <Paper
            variant="outlined"
            sx={{
              flex: { md: "0 0 75%" },
              maxWidth: { md: "75%" },
              p: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignSelf: "stretch",
            }}
          >
            <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Vin
              </Typography>
            </Box>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <InfoRow label="Vin">{v.wine_name}</InfoRow>
                <InfoRow label="Producent">{v.producer_name}</InfoRow>
                <InfoRow label="Årgang">{yearLabel}</InfoRow>
                <InfoRow label="Farve">{COLOR_LABELS[v.color_name] || v.color_name}</InfoRow>
                <InfoRow label="Stil">{STYLE_LABELS[v.style_name] || v.style_name}</InfoRow>
                <InfoRow label="Land">{countryName || v.country_name}</InfoRow>
                <InfoRow label="Region">{v.geography_name}</InfoRow>
                <InfoRow label="Druer">
                  {(v.grapes || []).length ? v.grapes.join(", ") : "—"}
                </InfoRow>
                <InfoRow label="Beskrivelse">
                  {v.description ? (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {v.description}
                    </Typography>
                  ) : (
                    "—"
                  )}
                </InfoRow>
                {v.grapeminds_id ? <InfoRow label="GrapeMinds">{v.grapeminds_id}</InfoRow> : null}
              </tbody>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              flex: { md: "1 1 25%" },
              minWidth: { md: 0 },
              p: 2,
              display: "flex",
              flexDirection: "column",
              alignSelf: "stretch",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Min samling
            </Typography>
            <Box
              component="form"
              onSubmit={handleSaveCollection}
              sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
            >
              <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                <TextField
                  type="number"
                  size="small"
                  label="Antal"
                  value={quantity}
                  onChange={(e) => changeQuantity(e.target.value)}
                  inputProps={{ min: 0, "aria-label": "Antal" }}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Noter"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  minRows={2}
                  placeholder="Kælder, pris…"
                  fullWidth
                />
                <Button
                  type="button"
                  variant="outlined"
                  onClick={toggleWantlist}
                  fullWidth
                >
                  {isWantlist ? "Fjern fra ønskeliste" : "Tilføj til ønskeliste"}
                </Button>
                {item.label_media_uuid ? (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 120,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: "grey.50",
                      p: 0.75,
                    }}
                  >
                    <PostPhoto
                      src={`/media/${item.label_media_uuid}`}
                      alt="Vinetikette"
                      maxSize={220}
                    />
                  </Box>
                ) : (
                  <Box sx={{ flex: 1 }} />
                )}
                <Button type="submit" variant="contained" disabled={savingCollection} fullWidth>
                  {savingCollection ? <CircularProgress size={18} /> : "Gem"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Smagninger
          </Typography>
          <Button component={RouterLink} to={tastingHref} variant="contained">
            Tilføj smagning
          </Button>
        </Stack>
        {tastings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Ingen smagninger endnu.
          </Typography>
        ) : (
          <List dense disablePadding>
            {tastings.map((t) => (
              <ListItemButton
                key={t.tasting_uuid}
                component={RouterLink}
                to={`/wine-tasting/${t.tasting_uuid}`}
              >
                <ListItemText
                  primary={t.title}
                  secondary={
                    t.created_at ? new Date(t.created_at).toLocaleString() : undefined
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}

export default WineManagerDetail;
